import { useEffect, useMemo, useState } from 'react';
import { Dropdown as AntDropdown } from 'antd';
import styled from 'styled-components';
import Notification from 'components/notification';
import Icon from 'components/icons';
import { ContentAccessMode, Team } from 'contexts/teams/types';
import { updateTeamContentAccess } from 'services/teams';
import { getTeamId } from 'utils/teams';
import { ContentAccessRadio } from '../TeamSharedStyles';

const UNRESTRICTED_CONTENT_ACCESS: ContentAccessMode = 'unrestricted';
const ASSIGNED_CONTENT_ONLY: ContentAccessMode = 'assigned_content_only';
const OPEN_TRIGGER_SHADOW = '0px 0px 2px rgba(0, 0, 0, 0.25)';
const DROPDOWN_SHADOW = '0px 30px 24px rgba(0, 0, 0, 0.2)';
const CONTENT_ACCESS_UPDATED_MESSAGE = 'Content access updated';
const ROLLBACK_ERROR_MESSAGE =
  "We couldn't update content access for this team. No learners were restricted. Please try again.";
const GENERIC_ERROR_MESSAGE = "We couldn't update content access for this team. Please try again.";
const RESTRICTED_WARNING =
  "If users belong to other teams with unlimited content access, they'll still have full access to all content.";

const ClosedButton = styled.button<{ $open: boolean; $readOnly: boolean }>`
  width: 258px;
  height: 44px;
  border-radius: 10px;
  padding: 12px 10px;
  border: ${({ $open }) => ($open ? `1px solid var(--lilac)` : '1px solid transparent')};
  box-shadow: ${({ $open }) => ($open ? OPEN_TRIGGER_SHADOW : 'none')};
  background: var(--grey-lighter);
  color: var(--light-gray-darker);
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: ${({ $readOnly }) => ($readOnly ? 'default' : 'pointer')};
`;

const DropdownMenu = styled.div`
  width: 258px;
  padding: 12px;
  border-radius: 10px;
  background: var(--white);
  box-shadow: ${DROPDOWN_SHADOW};
`;

const OptionCard = styled.label<{ $selected: boolean }>`
  width: 234px;
  padding: 10px;
  border-radius: 7px;
  border: ${({ $selected }) =>
    $selected ? `2px solid var(--lilac)` : `1px solid var(--dusty-gray)`};
  background: ${({ $selected }) => ($selected ? 'var(--fog-15)' : 'var(--white)')};
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-bottom: 12px;
  cursor: pointer;
`;

const TriggerLabel = styled.span`
  display: inline-flex;
  align-items: center;
  min-width: 0;
`;

const OptionTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: var(--black-dark);
`;

const OptionDescription = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  color: var(--light-gray-darker);
`;

const WarningDivider = styled.div`
  height: 1px;
  width: 100%;
  background: var(--lilac-lighter);
  margin: 8px 0;
`;

const SaveButton = styled.button`
  width: 234px;
  height: 36px;
  border: 0;
  border-radius: 8px;
  background: var(--black-dark);
  color: var(--white);
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

type TeamContentAccessDropdownProps = {
  team: Team;
  canManage: boolean;
  onSaved: (teamId: string, contentAccessMode: ContentAccessMode) => void;
};

const modeToLabel = (mode: ContentAccessMode): string => {
  return mode === ASSIGNED_CONTENT_ONLY ? 'Assigned content only' : 'Unlimited';
};

const TeamContentAccessDropdown = ({
  team,
  canManage,
  onSaved,
}: TeamContentAccessDropdownProps): JSX.Element => {
  const initialMode = useMemo<ContentAccessMode>(() => {
    return team.content_access_mode ?? UNRESTRICTED_CONTENT_ACCESS;
  }, [team.content_access_mode]);
  const [persistedMode, setPersistedMode] = useState<ContentAccessMode>(initialMode);
  const [selectedMode, setSelectedMode] = useState<ContentAccessMode>(initialMode);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const nextMode = team.content_access_mode ?? UNRESTRICTED_CONTENT_ACCESS;
    setPersistedMode(nextMode);
    setSelectedMode(nextMode);
  }, [team.id, team.team_id, team.content_access_mode]);

  const handleVisibleChange = (visible: boolean) => {
    setOpen(visible);
    if (!visible) {
      setSelectedMode(persistedMode);
    }
  };

  const handleSave = async () => {
    const teamId = getTeamId(team);

    if (!teamId || selectedMode === persistedMode || saving) {
      return;
    }

    setSaving(true);

    try {
      const response = await updateTeamContentAccess({
        id: teamId,
        content_access_mode: selectedMode,
      });
      const responseMode = response.data.content_access_mode;

      setPersistedMode(responseMode);
      setSelectedMode(responseMode);
      onSaved(teamId, responseMode);
      Notification({ type: 'success', text: CONTENT_ACCESS_UPDATED_MESSAGE, singleLine: true });
      setOpen(false);
    } catch (error) {
      setSelectedMode(persistedMode);
      const isRollbackError =
        error?.response?.status === 409 &&
        error?.response?.data?.code === 'content_restriction_rollback';
      Notification({
        type: 'error',
        text: isRollbackError ? ROLLBACK_ERROR_MESSAGE : GENERIC_ERROR_MESSAGE,
        singleLine: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const isRestricted = persistedMode === ASSIGNED_CONTENT_ONLY;

  const trigger = (
    <ClosedButton
      data-testid={'content-access-trigger-' + getTeamId(team)}
      type="button"
      disabled={!canManage}
      aria-disabled={!canManage}
      $open={open}
      $readOnly={!canManage}
    >
      <TriggerLabel>
        {open && isRestricted && <span data-testid={'content-access-warning-' + getTeamId(team)} />}
        <span>{modeToLabel(persistedMode)}</span>
      </TriggerLabel>
      <Icon
        icon="ChevronDown"
        color="var(--light-gray-darker)"
        style={{ width: '10px', height: '6px' }}
      />
    </ClosedButton>
  );

  if (!canManage) {
    return trigger;
  }

  const overlay = (
    <DropdownMenu>
      <OptionCard
        data-testid="content-access-option-unrestricted"
        $selected={selectedMode === UNRESTRICTED_CONTENT_ACCESS}
      >
        <ContentAccessRadio
          type="radio"
          name={`content-access-${getTeamId(team)}`}
          checked={selectedMode === UNRESTRICTED_CONTENT_ACCESS}
          onChange={() => setSelectedMode(UNRESTRICTED_CONTENT_ACCESS)}
        />
        <div>
          <OptionTitle>Unlimited access</OptionTitle>
          <OptionDescription>
            Learners can browse and access the full training catalog.
          </OptionDescription>
        </div>
      </OptionCard>

      <OptionCard
        data-testid="content-access-option-assigned"
        $selected={selectedMode === ASSIGNED_CONTENT_ONLY}
      >
        <ContentAccessRadio
          type="radio"
          name={`content-access-${getTeamId(team)}`}
          checked={selectedMode === ASSIGNED_CONTENT_ONLY}
          onChange={() => setSelectedMode(ASSIGNED_CONTENT_ONLY)}
        />
        <div>
          <OptionTitle>Assigned content only</OptionTitle>
          <OptionDescription>
            Learners only see content assigned through playlists.
          </OptionDescription>
          {selectedMode === ASSIGNED_CONTENT_ONLY && (
            <>
              <WarningDivider />
              <OptionDescription>{RESTRICTED_WARNING}</OptionDescription>
            </>
          )}
        </div>
      </OptionCard>

      <SaveButton
        data-testid="content-access-save"
        type="button"
        disabled={saving || selectedMode === persistedMode}
        onClick={handleSave}
      >
        Save
      </SaveButton>
    </DropdownMenu>
  );

  return (
    <AntDropdown
      overlay={overlay}
      trigger={['click']}
      visible={open}
      onVisibleChange={handleVisibleChange}
      placement="bottomLeft"
    >
      {trigger}
    </AntDropdown>
  );
};

export default TeamContentAccessDropdown;
